<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div>
    <div class="container">
      <div class="row">

        <!-- Ownership transfer notices -->
        <div class="col-12" v-if="ownershipTransferStatus">
          <div class="alert alert-success" role="alert">{{ ownershipTransferStatus }}</div>
        </div>
        <div class="col-12" v-if="pendingOwnershipTransfers && pendingOwnershipTransfers.length">
          <div class="alert alert-info" role="alert">
            <h5 class="mb-2">Pending Ownership Transfer {{ pendingOwnershipTransfers.length === 1 ? 'Invitation' : 'Invitations' }}</h5>
            <p class="mb-2">You have been invited to take ownership of the following. Nothing changes until you accept.</p>
            <ul class="mb-0">
              <li v-for="(invite, index) in pendingOwnershipTransfers" :key="index" class="mb-1">
                <strong>{{ invite.requestedByName }}</strong> invited you to own the {{ invite.entityLabel }}
                <a :href="invite.entityUrl">{{ invite.entityName }}</a> (sent {{ invite.requestedAt }}, expires {{ invite.expiresAt }}) &mdash;
                <a :href="invite.reviewUrl" class="btn btn-sm btn-registry ml-1">Review &amp; Accept</a>
              </li>
            </ul>
          </div>
        </div>

        <!-- My Resources Panel -->
        <div class="col-12">
          <div class="card bg-color-light border-dark mb-3">
              <h3 class="font-primary font-resource pl-4 pr-4 pt-4">
                My Resources
                <span class="d-none d-md-block smaller float-right">
                  CREATE RESOURCE
                  <a :href="baseUrls.resourceBaseUrl + 'create'"><div class="ml-3 icon-plus-resource icon-small-offset-down"></div></a>
                </span>
                <span class="d-block d-md-none smaller float-right">
                  <div class="ml-3 icon-plus-resource icon-small-offset-down shadow-resource"></div>
                </span>
              </h3>
              <div class="row bg-color-light border-rounded-full p-2 m-3">

                <!-- inner cards -->
                <div class="col-lg-4 col-md-8 col-sm-2 pr-1 pl-1">
                  <div class="bg-color-light border-rounded-full p-2 h-100 border-dark">
                    <div class="row h-90">
                      <div class="col-lg-6 col-md-12">
                        <div class="card card-ui card-ui-no-border card-solid bg-color-light h-100">
                          <div class="card-body d-flex flex-column">
                            <h1>{{resourceInfo.total}}</h1>
                            <p>TOTAL RESOURCES</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-lg-6 col-md-12">
                        <div class="card card-ui card-ui-no-border bg-color-light h-100">
                          <table class="table table-sm table-borderless my-auto">
                            <tbody>
                              <tr>
                                <td>
                                  <h4 class="color-inherit mb-0">{{resourceInfo.numUnpublished}}</h4>
                                  <p class="mb-2">UNPUBLISHED</p>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <h4 class="color-inherit mb-0">{{resourceInfo.numPublished}}</h4>
                                  <p class="mb-2">PUBLISHED</p>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div class="row h-10 justify-content-center">
                      <a :href="baseUrls.resourceBaseUrl" class="btn btn-resource d-block mx-auto" style="width: 80%;">MANAGE</a>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <div class="card-header card-header-success">
                      <div class="icon-resource icon-success icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>{{resourceInfo.numPublications}}</h3>
                      <p class="small">RESOURCE PUBLICATIONS</p>
                      <a :href="baseUrls.resourceBaseUrl + 'index/published#list'" class="btn btn-resource btn-success btn-sm mt-auto">VIEW</a>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <div v-if="resourceInfo.pendingPublishRequests > 0" class="card-header card-header-warning">
                      <div class="icon-resource icon-warning icon-offset-down shadow-resource"></div>
                    </div>
                    <div v-else class="card-header card-header-disabled">
                      <div class="icon-resource icon-disabled icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>{{resourceInfo.pendingPublishRequests}}</h3>
                      <p class="small">PENDING PUBLICATION REQUESTS</p>
                      <a v-if="resourceInfo.pendingPublishRequests > 0" :href="baseUrls.resourceBaseUrl + 'index/pending#list'" class="btn btn-resource btn-warning btn-sm mt-auto">VIEW</a>
                      <button v-else href="#" class="btn btn-resource btn-disabled btn-sm mt-auto" disabled>VIEW</button>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <div v-if="resourceInfo.deniedPublishRequests > 0" class="card-header card-header-danger">
                      <div class="icon-resource icon-danger icon-offset-down shadow-resource"></div>
                    </div>
                    <div v-else class="card-header card-header-disabled">
                      <div class="icon-resource icon-disabled icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>{{resourceInfo.deniedPublishRequests}}</h3>
                      <p class="small">DENIED PUBLICATION REQUESTS</p>
                      <a v-if="resourceInfo.deniedPublishRequests > 0" :href="baseUrls.resourceBaseUrl + 'index/denied#list'" class="btn btn-resource btn-danger btn-sm mt-auto">VIEW</a>
                      <button v-else href="#" class="btn btn-resource btn-disabled btn-sm mt-auto" disabled>VIEW</button>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <div class="card-header card-header-disabled">
                      <div class="icon-resource icon-disabled icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>0</h3>
                      <p class="small">TAKEN DOWN PUBLICATIONS</p>
                      <button class="btn btn-resource btn-disabled btn-sm mt-auto" disabled>VIEW</button>
                    </div>
                  </div>
                </div>

              </div>  <!-- end outer row - my resources -->
          </div>  <!-- end outer card - my resources -->
        </div>  <!-- end outer column - my resources -->

        <!-- My Registries Panel -->
        <div class="col-12">
          <div class="card border-dark bg-color-light mb-3">
              <h3 class="font-primary font-registry pl-4 pr-4 pt-4">
                My Registries
                <span class="d-none d-md-block smaller float-right">
                  CREATE REGISTRY
                  <a :href="baseUrls.irrRequestBaseUrl + 'create'"><div class="ml-3 icon-plus-registry icon-small-offset-down"></div></a>
                </span>
                <span class="d-block d-md-none smaller float-right">
                  <div class="ml-3 icon-plus-registry icon-small-offset-down shadow-resource"></div>
                </span>
              </h3>
              <div class="row bg-color-light border-rounded-full p-2 m-3">

                <!-- inner cards -->
                <div class="col-lg-4 col-md-8 col-sm-2 pr-1 pl-1">
                  <div class="bg-color-light border-rounded-full p-2 h-100 border-dark">
                    <div class="row h-90">
                      <div class="col-lg-6 col-md-12">
                        <div class="card card-ui card-ui-no-border card-solid bg-color-light h-100">
                          <div class="card-body d-flex flex-column">
                            <h1>{{irrInfo.total}}</h1>
                            <p>TOTAL REGISTRIES</p>
                          </div>
                        </div>
                      </div>
                      <div class="col-lg-6 col-md-12">
                        <div class="card card-ui card-ui-no-border bg-color-light h-100">
                          <table class="table table-sm table-borderless my-auto">
                            <tbody>
                              <!--
                              <tr>
                                <td>
                                  <h4 class="color-inherit mb-0">3</h4>
                                  <p class="mb-2">DRAFTS</p>
                                </td>
                              </tr>
                              -->
                              <tr>
                                <td>
                                  <h4 class="color-inherit mb-0">{{irrInfo.numPending}}</h4>
                                  <p class="mb-2">UNDER REVIEW</p>
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <h4 class="color-inherit mb-0">{{irrInfo.numApproved}}</h4>
                                  <p class="mb-2">APPROVED</p>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div class="row h-10 justify-content-center">
                      <a :href="baseUrls.irrBaseUrl" class="btn btn-resource d-block mx-auto" style="width: 80%;">MANAGE</a>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <div v-if="irrInfo.numApproved > 0" class="card-header card-header-success">
                      <div class="icon-registry icon-success icon-offset-down shadow-resource"></div>
                    </div>
                    <div v-else class="card-header card-header-disabled">
                      <div class="icon-registry icon-disabled icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>{{irrInfo.numApproved}}</h3>
                      <p class="small">APPROVED REGISTRY REQUESTS</p>
                      <a v-if="irrInfo.numApproved > 0" href="/irr-request" class="btn btn-resource btn-success btn-sm mt-auto">VIEW</a>
                      <button v-else href="#" class="btn btn-resource btn-disabled btn-sm mt-auto" disabled>VIEW</button>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <div v-if="irrInfo.numPending > 0" class="card-header card-header-warning">
                      <div class="icon-registry icon-warning icon-offset-down shadow-resource"></div>
                    </div>
                    <div v-else class="card-header card-header-disabled">
                      <div class="icon-registry icon-disabled icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>{{irrInfo.numPending}}</h3>
                      <p class="small">REGISTRY REQUESTS UNDER REVIEW</p>
                      <a v-if="irrInfo.numPending > 0" href="/irr-request" class="btn btn-resource btn-warning btn-sm mt-auto">VIEW</a>
                      <button v-else href="#" class="btn btn-resource btn-disabled btn-sm mt-auto" disabled>VIEW</button>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <div v-if="irrInfo.numDenied > 0" class="card-header card-header-danger">
                      <div class="icon-registry icon-danger icon-offset-down shadow-resource"></div>
                    </div>
                    <div v-else class="card-header card-header-disabled">
                      <div class="icon-registry icon-disabled icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>{{irrInfo.numDenied}}</h3>
                      <p class="small">DENIED REGISTRY REQUESTS</p>
                      <a v-if="irrInfo.numDenied > 0" href="/irr-request" class="btn btn-resource btn-danger btn-sm mt-auto">VIEW</a>
                      <button v-else href="#" class="btn btn-resource btn-disabled btn-sm mt-auto" disabled>VIEW</button>
                    </div>
                  </div>
                </div>

                <div class="col-lg-2 col-md-4 col-sm-1 pr-1 pl-1">
                  <div class="card bg-color-light card-ui shadow d-flex h-100 border-dark">
                    <a v-if="irrInfo.numPendResourcePubReqs > 0"
                       href="/irr/all-publish-requests"
                       class="card-alert-badge"
                       aria-label="View pending resource publication requests"
                       title="View pending resource publication requests">!</a>
                    <div v-if="irrInfo.numPendResourcePubReqs > 0" class="card-header card-header-warning">
                      <div class="icon-registry icon-warning icon-offset-down shadow-resource"></div>
                    </div>
                    <div v-else class="card-header card-header-disabled">
                      <div class="icon-registry icon-disabled icon-offset-down shadow-resource"></div>
                    </div>
                    <div class="card-body d-flex flex-column">
                      <h3>{{irrInfo.numPendResourcePubReqs}}</h3>
                      <p class="small">PENDING RESOURCE PUBLICATION REQUESTS</p>
                      <a v-if="irrInfo.numPendResourcePubReqs > 0" href="/irr/all-publish-requests" class="btn btn-resource btn-warning btn-sm mt-auto">VIEW</a>
                      <button v-else href="#" class="btn btn-resource btn-disabled btn-sm mt-auto" disabled>VIEW</button>
                    </div>
                  </div>
                </div>

              </div>  <!-- end outer row - my registries -->
          </div>  <!-- end outer card - my registries -->
        </div>  <!-- end outer column - my registries -->

        <!-- My Templates Panel -->
        <div class="col-12">
          <div class="card bg-color-light border-dark">
              <h3 class="font-primary font-template pl-4 pr-4 pt-4">
                My Templates
                <span class="d-none d-md-block smaller float-right">
                  CREATE TEMPLATE
                  <a :href="baseUrls.templateBaseUrl + 'create'"><div class="ml-3 icon-plus-template icon-small-offset-down"></div></a>
                </span>
                <span class="d-block d-md-none smaller float-right">
                  <div class="ml-3 icon-plus-template icon-small-offset-down shadow-resource"></div>
                </span>
              </h3>
              <div class="row bg-color-light border-rounded-full p-2 m-3">

                <!-- inner cards -->
                <div class="col-lg-4 col-md-8 col-sm-2 pr-1 pl-1">
                  <div class="bg-color-light border-dark border-rounded-full p-2 h-100">
                    <div class="row h-90">
                      <div class="col-lg-12 col-md-12">
                        <div class="card card-ui card-ui-no-border card-solid bg-color-light h-100">
                          <div class="card-body d-flex flex-column">
                            <h1>{{templateInfo.total}}</h1>
                            <p>TOTAL TEMPLATES</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row h-10 justify-content-center">
                      <a :href="baseUrls.templateBaseUrl" class="btn btn-resource d-block mx-auto" style="width: 80%;">MANAGE</a>
                    </div>
                  </div>
                </div>
              </div>  <!-- end outer row - my templates -->
          </div>  <!-- end outer card - my templates -->
        </div>  <!-- end outer column - my templates -->

      </div>  <!-- end row -->
    </div>  <!-- end container -->
  </div>
</template>

<script>
    export default {
      props: {
        permissions: Object,
        resourceInfo: Object,
        irrInfo: Object,
        templateInfo: Object,
        // pending ownership transfer requests addressed to the current user - see App\Support\OwnershipTransfers::incomingForUser()
        pendingOwnershipTransfers: {
          type: Array,
          default: () => []
        },
        ownershipTransferStatus: {
          type: String,
          default: ''
        },
      },
      data: function() {
        return {
          baseUrls: {
            resourceBaseUrl: '/resource/',
            irrBaseUrl: '/irr/',
            irrRequestBaseUrl: '/irr-request/',
            templateBaseUrl: '/template/',
            userBaseUrl: '/user/'
          },
          csrf_token: $('meta[name="csrf-token"]').attr('content'),
        }
      },
      mounted(){
        //
      }
    }
</script>
